import { defineMessages, useIntl } from 'react-intl';

import { getPaymentMethodIcon } from '@garnish/commerce';
import { type IconName } from '@garnish/core';

import { CardType } from '@sg/graphql-schema';

import { type PaymentMethod } from '../../types';
import { fallbackToSavedPaymentMethod, removeEndingIn } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const useStripePaymentMethod = (props: UseStripePaymentMethodProps) => {
  const {
    hasStripeFailedToLoad,
    paymentMethods,
    name,
    icon,
    isAddingNewPaymentMethod,
    isGooglePay,
  } = props;

  const { formatMessage } = useIntl();

  // Show default/first saved payment method if valid and no payment method is selected from the SDK.
  const fallbackPaymentMethod = fallbackToSavedPaymentMethod(paymentMethods);
  const isValidFallback = fallbackPaymentMethod?.cardType !== CardType.Unsupported; // prettier-ignore

  const fallbackName = isValidFallback ? fallbackPaymentMethod?.description : undefined; // prettier-ignore
  const fallbackIcon = isValidFallback ? getPaymentMethodIcon(fallbackPaymentMethod?.cardType) : undefined; // prettier-ignore

  const missingName = hasStripeFailedToLoad
    ? formatMessage(messages.stripeFailedToLoad)
    : formatMessage(messages.selectPaymentMethod);

  const paymentMethod = removeEndingIn(name || fallbackName || missingName);
  const paymentMethodIcon = getPaymentMethodIconWithFallback(icon, fallbackIcon, isGooglePay);
  const hasNoPaymentMethod = !name && !fallbackName;

  // On web, a partially filled new card form blocks the payment element confirmation.
  if (isAddingNewPaymentMethod) {
    return {
      paymentMethod: name || formatMessage(messages.selectPaymentMethod),
      paymentMethodIcon: getPaymentMethodIconWithFallback(icon, undefined, false),
      hasNoPaymentMethod: !name,
    };
  }

  return {
    paymentMethod,
    paymentMethodIcon,
    hasNoPaymentMethod,
  };
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function getPaymentMethodIconWithFallback(
  icon: IconName | undefined,
  fallbackIcon: IconName | undefined,
  isGooglePay: boolean | undefined,
): IconName {
  if (isGooglePay) {
    return 'IconGooglePay';
  }

  if (icon && icon !== 'IconCreditCard') {
    return icon;
  }

  if (fallbackIcon) {
    return fallbackIcon;
  }

  return 'IconCreditCard';
}

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  selectPaymentMethod: {
    defaultMessage: 'Select payment method',
    description: 'Bag | Selected Payment Method | Select payment method',
  },
  stripeFailedToLoad: {
    defaultMessage: 'Something went wrong.',
    description: 'Bag | Selected Payment Method | Stripe failed to load',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type UseStripePaymentMethodProps = Readonly<{
  hasStripeFailedToLoad: boolean;
  paymentMethods: readonly PaymentMethod[];
  name?: string;
  icon?: IconName;
  imageBase64?: string;
  isAddingNewPaymentMethod: boolean;
  isGooglePay?: boolean;
}>;
